import type { AppType } from '@amore/api'
import { problemDetailsSchema } from '@amore/shared'
import { hc } from 'hono/client'
import type { z } from 'zod'
import { problemMessage } from './error-messages'

/**
 * The typed client. `AppType` is a type-only import, so nothing from the API
 * ends up in this bundle — but renaming a response field turns this app red.
 *
 * Empty base URL keeps API calls same-origin. Vite proxies /api in dev, and
 * Cloudflare Pages proxies /api to Fly in production.
 */
export const api = hc<AppType>('', {
  init: { credentials: 'include' },
})

/** Every failure the API can produce arrives in this shape. */
type ProblemDetails = z.infer<typeof problemDetailsSchema>

export class ApiError extends Error {
  constructor(readonly problem: ProblemDetails) {
    super(problemMessage(problem))
    this.name = 'ApiError'
  }
}

type JsonResponse<T> = {
  readonly ok: boolean
  readonly status: number
  json(): Promise<T>
}

/**
 * Unwraps a Hono RPC response, preserving the inferred success type and
 * turning problem+json into a typed throw for TanStack Query.
 */
export async function unwrap<T>(promise: Promise<JsonResponse<T>>): Promise<T> {
  const response = await promise

  if (!response.ok) {
    throw new ApiError(await readProblem(response))
  }

  return response.json()
}

export async function unwrapVoid(promise: Promise<JsonResponse<unknown>>): Promise<void> {
  const response = await promise

  if (!response.ok) {
    throw new ApiError(await readProblem(response))
  }
}

async function readProblem(response: Pick<Response, 'json' | 'status'>): Promise<ProblemDetails> {
  try {
    const value: unknown = await response.json()

    const result = problemDetailsSchema.safeParse(value)
    if (result.success) {
      return result.data
    }
  } catch {
    // The fallback below intentionally hides malformed error response details.
  }

  return {
    type: 'unknown-error',
    code: 'unknown-error',
    status: response.status,
  }
}

export function apiErrorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback
}
